import RolePopupManager from './RolePopupManager.js';
import { MarqueeSelect } from './MarqueeSelect.js'; 


export class SandboxView {
    constructor(config) {
        this.containerId = config.container;
        this.data = config.data ? JSON.parse(JSON.stringify(config.data)) : [];
        this.rootId = config.rootId;
        this.actions = config.actions || {};
        this.displaySettings = config.displaySettings || { cc: '#F59E0B', tc: '#fff', bc: '#e5e7eb' };
        
        this.cardWidth = 340; 
        this.cardHeight = 180; 
        this.gapX = 80;
        this.gapY = 160;
        
        this.zoom = 0.6;
        this.panX = 0;
        this.panY = 0;
        this.isBackgroundDragging = false;
        this.marquee = null;
        this.activeTool = 'pan';
        this.resizeObserver = null; // <-- ADD THIS LINE
        
        this.init();
    }

    init() {
        this.injectStyles();
        this.renderLayout();
        
        window.addEventListener('kvdr-sandbox-refresh', (e) => this.handleRefresh(e));

        // Use requestAnimationFrame for more reliable layout measurement
        // requestAnimationFrame(() => {
        //     this.refreshData();
        //     this.resetView(); 
        // });
        this.refreshData();
        
        this.attachEvents();
        
        this.marquee = new MarqueeSelect({
            wrapper: this.wrapper,
            getNodes: () => Object.values(this.nodeLookup || {}).map(node => ({
                id: node.selfId,
                data: node
            })),
            onSelectionChange: (selectedIds) => this._updateNodeSelectionStyles(selectedIds),
            onSelectionComplete: (selectedNodes) => {
                if (selectedNodes.length > 0) {
                    window.dispatchEvent(new CustomEvent('show-role-inspector', { 
                        detail: { nodes: selectedNodes } 
                    }));
                    
                    this.activeTool = 'pan';
                    const selectToolBtn = this.wrapper.querySelector('#sb-select-tool');
                    if (selectToolBtn) selectToolBtn.classList.remove('active');
                    this.wrapper.style.cursor = 'grab';
                }
            }
        });
    }

    handleRefresh(e) {
        if (e.detail && e.detail.graphData) {
            this.data = JSON.parse(JSON.stringify(e.detail.graphData));
        }
        this.refreshData();
    }

    destroy() {
        if (this.wrapper) this.wrapper.remove();
        
        if (this.marquee && typeof this.marquee.destroy === 'function') {
            this.marquee.destroy();
        }
        
        // ADD THIS CLEANUP LOGIC
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        clearTimeout(this.resizeTimeout);
    }

    refreshData() {
        this.calculateTreeLayout();
        this.draw();
        this.drawMinimap();
    }

    injectStyles() {
        const oldStyle = document.getElementById('sb-styles-ui-v2');
        if (oldStyle) oldStyle.remove();

        const css = `
            .sb-wrapper {
                position: absolute !important; 
                top: 0; left: 0;
                width: 100%; 
                height: 100%;
                background-color: #f8f9fa;
                background-image: radial-gradient(#cbd5e1 1.5px, transparent 1.5px);
                background-size: 24px 24px; 
                font-family: 'Inter', sans-serif;
                overflow: hidden !important; 
                display: block; 
                border-radius: 8px; 
                border: 1px solid #e5e7eb;
                box-sizing: border-box;
            }
            .sb-canvas { transform-origin: 0 0; position: absolute; top: 0; left: 0; width: 100%; height: 100%; }
            .sb-svg-layer { position: absolute; top: 0; left: 0; pointer-events: none; overflow: visible; z-index: 0; }
            .sb-connector { fill: none; stroke: #94a3b8; stroke-width: 2px; }

            .sb-controls {
                position: absolute; bottom: 24px; left: 24px;
                display: flex; flex-direction: column; 
                background: white; border-radius: 6px; overflow: hidden;
                box-shadow: 0 4px 12px rgba(0,0,0,0.1); z-index: 10000;
                border: 1px solid #e0e0e0;
            }
            .sb-ctrl-btn {
                width: 36px; height: 36px; border: none; background: white;
                display: flex; align-items: center; justify-content: center;
                cursor: pointer; border-bottom: 1px solid #f0f0f0; color: #555;
            }
            .sb-ctrl-btn:last-child { border-bottom: none; }
            .sb-ctrl-btn:hover { background: #f9f9f9; color: #000; }
            .sb-ctrl-btn.active {
                background-color: var(--sb-primary);
                color: #fff;
            }
            .sb-ctrl-btn svg { width: 18px; height: 18px; }
            .sb-minimap {
                position: absolute; bottom: 24px; right: 24px;
                width: 200px; height: 150px; background: rgba(255,255,255,0.9);
                border: 1px solid #e0e0e0; border-radius: 6px;
                box-shadow: 0 4px 12px rgba(0,0,0,0.1); z-index: 10000;
                overflow: hidden; pointer-events: none;
            }
            .sb-mini-node { position: absolute; background: #cbd5e1; border-radius: 2px; }
            .sb-mini-viewport { position: absolute; border: 2px solid var(--sb-primary); background: rgba(0,0,0,0.05); box-sizing: border-box; }           
            
            .sb-node {
                position: absolute; 
                width: ${this.cardWidth}px !important; 
                height: ${this.cardHeight}px !important;
                background: #fff; border: 1px solid #e5e7eb; border-radius: 12px;
                box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); display: flex; flex-direction: column;
                transition: box-shadow 0.2s, transform 0.2s, border-color 0.2s, opacity 0.2s; z-index: 1; overflow: hidden;
                box-sizing: border-box; user-select: none; 
                cursor: grab;
            }
            .sb-node:active { cursor: grabbing; }
            .sb-node.is-dragging { opacity: 0.5; border: 2px dashed #999; }
            .sb-node.drag-over-active { background-color: #e6fffa; border: 2px solid #00b894; transform: scale(1.05); z-index: 100; }
            .sb-node:hover {
                box-shadow: 0 20px 30px -5px rgba(0, 0, 0, 0.15); transform: translateY(-2px); z-index: 10; border-color: var(--sb-primary);
            }
            
            /* Add highlight for selection */
            .sb-node.is-selected {
                border: 2px solid var(--sb-primary);
                box-shadow: 0 0 15px rgba(245, 158, 11, 0.5);
            }
            
            .sb-node-header {
                padding: 0 16px; display: flex; align-items: center; 
                justify-content: space-between; height: 46px; flex-shrink: 0; color: #000;
            }
            .sb-title-span { font-weight: 700; font-size: 15px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
            
            .sb-header-actions { display: flex; gap: 4px; margin-left: 10px; }
            .sb-act-btn { background: rgba(255,255,255,0.25); border: none; border-radius: 4px; cursor: pointer; padding: 4px; color: white; display:flex;}
            .sb-act-btn:hover { background: rgba(255,255,255,0.5); }

            .sb-node-body { 
                padding: 16px; font-size: 13px; color: #475569; display: flex; flex-direction: column; gap: 8px; flex: 1; overflow: hidden;
                cursor: pointer;
            }
            .sb-node-body:hover { background-color: #f8fafc; }
            .sb-desc-box {
                width: 100%; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
                font-family: inherit; font-size: 12px; color: #64748b; line-height: 1.5;
            }

            .sb-footer-row { display: flex; align-items: center; justify-content: space-between; margin-top: auto; padding-top: 10px; border-top: 1px solid #f3f4f6; flex-shrink: 0;}
            .sb-avatars-row { display: flex; align-items: center; }
            .sb-avatar { width: 30px; height: 30px; border-radius: 50%; border: 2px solid #fff; margin-left: -8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); object-fit: cover; background: #e2e8f0; cursor: pointer; transition: transform 0.1s; position: relative;}
            .sb-avatar:hover { transform: translateY(-3px); z-index: 50 !important; }
            .sb-avatar:first-child { margin-left: 0; }
            .sb-avatar-more { width: 30px; height: 30px; border-radius: 50%; border: 2px solid #fff; background: #f1f5f9; color: #475569; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 600; margin-left: -8px; z-index: 1; position: relative;}

            .sb-stats { display: flex; align-items: center; }
            .sb-badge { background: #f1f5f9; padding: 4px 10px; border-radius: 20px; font-size: 11px; font-weight: 600; color: #475569; display:flex; gap:5px; align-items:center; }

            .sb-add-btn {
                position: absolute; width: 28px; height: 28px; 
                background: #fff; border: 1px solid #cbd5e1; border-radius: 50%; 
                display: flex; align-items: center; justify-content: center;
                cursor: pointer; box-shadow: 0 4px 6px rgba(0,0,0,0.05); color: #64748b;
                z-index: 5; transition: background 0.2s, transform 0.2s;
                transform: translateX(-50%); 
            }
            .sb-add-btn:hover { 
                background: var(--sb-primary); color: #fff; border-color: var(--sb-primary); transform: translateX(-50%) scale(1.15); 
            }
        `;
        const style = document.createElement('style');
        style.id = 'sb-styles-ui-v2';
        style.innerText = css;
        document.head.appendChild(style);
    }

    renderLayout() {
        const targetContainer = document.getElementById(this.containerId);
        if(!targetContainer) return;
        targetContainer.innerHTML = ''; 

        this.wrapper = document.createElement('div');
        this.wrapper.className = 'sb-wrapper';
        this.wrapper.style.setProperty('--sb-primary', this.displaySettings.cc);

        const controls = document.createElement('div');
        controls.className = 'sb-controls';
        // --- NEW SVG for Select Tool ---
        controls.innerHTML = `
            <button class="sb-ctrl-btn" id="sb-zoom-in" title="Zoom In"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg></button>
            <button class="sb-ctrl-btn" id="sb-zoom-out" title="Zoom Out"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"/></svg></button>
            <button class="sb-ctrl-btn" id="sb-select-tool" title="Select Tool">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" stroke-dasharray="4 3">
                    <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                </svg>
            </button>
        `;

        this.minimap = document.createElement('div');
        this.minimap.className = 'sb-minimap';

        this.canvas = document.createElement('div');
        this.canvas.className = 'sb-canvas';
        
        this.svgLayer = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        this.svgLayer.classList.add("sb-svg-layer");
        this.svgLayer.setAttribute("width", "50000"); 
        this.svgLayer.setAttribute("height", "50000");

        this.canvas.appendChild(this.svgLayer);
        this.wrapper.append(controls, this.minimap, this.canvas);
        targetContainer.appendChild(this.wrapper);

        controls.querySelector('#sb-zoom-in').onclick = () => this.handleZoom(0.1);
        controls.querySelector('#sb-zoom-out').onclick = () => this.handleZoom(-0.1);
        const selectToolBtn = controls.querySelector('#sb-select-tool');
        selectToolBtn.onclick = () => {
            if (this.activeTool === 'pan') {
                this.activeTool = 'select';
                selectToolBtn.classList.add('active');
                this.wrapper.style.cursor = 'crosshair';
            } else {
                this.activeTool = 'pan';
                selectToolBtn.classList.remove('active');
                this.wrapper.style.cursor = 'grab';
            }
        };
    }

    calculateTreeLayout() {
        // Restore fixed card dimensions
        this.cardWidth = 340; 
        this.cardHeight = 180; 
        this.gapX = 80;
        this.gapY = 160;

        const nodeMap = {};
        this.data.forEach(n => {
            const sId = String(n.selfId);
            let pId = n.parentId ? String(n.parentId) : "0";
            if (pId === "null" || pId === "undefined" || pId === "") pId = "0";
            nodeMap[sId] = { ...n, selfId: sId, parentId: pId, children: [], treeWidth: 0, x: 0, y: 0 };
        });

        const allNodes = Object.values(nodeMap);
        if (allNodes.length === 0) return;

        let root = nodeMap[String(this.rootId)];
        if (!root) root = allNodes.find(n => n.parentId === "0" || n.parentId === "null");
        if (!root) root = allNodes[0];

        allNodes.forEach(n => {
            if (n.selfId === root.selfId) return;
            let pId = n.parentId;
            if (pId === "0" || !nodeMap[pId]) pId = root.selfId; 
            if (nodeMap[pId]) nodeMap[pId].children.push(n);
        });

        this.treeRoot = root;
        this.nodeLookup = nodeMap;

        this.measureTree(this.treeRoot);
        this.arrangeTree(this.treeRoot, 0, 0);
    }

    measureTree(node) {
        if (node.children.length === 0) {
            node.treeWidth = this.cardWidth + this.gapX;
            return node.treeWidth;
        }
        let w = 0;
        node.children.forEach(c => { w += this.measureTree(c); });
        node.treeWidth = w;
        return w;
    }

    arrangeTree(node, startX, level) {
        node.y = level * (this.cardHeight + this.gapY);
        if (node.children.length === 0) {
            node.x = startX + (this.gapX / 2);
            return;
        }
        let currentX = startX;
        node.children.forEach(c => {
            this.arrangeTree(c, currentX, level + 1);
            currentX += c.treeWidth;
        });

        const first = node.children[0];
        const last = node.children[node.children.length - 1];
        node.x = ((first.x + last.x) / 2);
    }

    draw() {
        this.svgLayer.innerHTML = '';
        const oldNodes = this.canvas.querySelectorAll('.sb-node, .sb-add-btn');
        oldNodes.forEach(n => n.remove());
        if(this.treeRoot) this.drawRecursive(this.treeRoot);
        this.drawMinimap();
    }

    drawRecursive(node) {
        this.renderNodeCard(node);
        node.children.forEach(child => {
            this.drawLink(node, child);
            this.drawRecursive(child);
        });
    }

    renderNodeCard(node) {
        const el = document.createElement('div');
        el.className = 'sb-node';
        el.dataset.id = node.selfId;
        el.style.left = `${node.x}px`;
        el.style.top = `${node.y}px`;

        el.draggable = true;
        el.addEventListener("dragstart", (e) => {
            e.stopPropagation();
            e.dataTransfer.setData("text/plain", node.selfId);
            window.draggedSandboxNodeId = node.selfId;
            el.classList.add('is-dragging');
        });
        el.addEventListener("dragend", (e) => {
            e.stopPropagation();
            el.classList.remove('is-dragging');
            delete window.draggedSandboxNodeId;
            document.querySelectorAll('.drag-over-active').forEach(elem => elem.classList.remove('drag-over-active'));
        });
        el.addEventListener("dragover", (e) => {
            e.preventDefault(); e.stopPropagation();
            if (window.draggedSandboxNodeId && window.draggedSandboxNodeId !== node.selfId) el.classList.add('drag-over-active');
        });
        el.addEventListener("dragleave", (e) => {
            e.preventDefault(); e.stopPropagation();
            el.classList.remove('drag-over-active');
        });
        el.addEventListener("drop", (e) => {
            e.preventDefault(); e.stopPropagation();
            el.classList.remove('drag-over-active');
            const draggedId = e.dataTransfer.getData("text/plain");
            if(draggedId && draggedId !== node.selfId) {
                const draggedNode = this.data.find(n => n.selfId === draggedId);
                const draggedName = draggedNode ? draggedNode.title : "this role";
                
                RolePopupManager.showConfirmMovePopup(draggedName, node.title, () => {
                    this.actions.onUpdate && this.actions.onUpdate(draggedId, { parentId: node.selfId });
                    const targetLocal = this.data.find(n => n.selfId === draggedId);
                    if(targetLocal) targetLocal.parentId = node.selfId;
                    window.dispatchEvent(new CustomEvent('kvdr-sandbox-refresh', { detail: { graphData: this.data } }));
                });
            }
        });

        const svgTrash = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>`;
        const svgEdit = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>`;
        const svgUser = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>`;

        const isRootNode = (node.selfId === this.rootId) || node.isRoot;
        const displayCount = node.candidateCount || 0; 
        const profiles = node.profiles || [];
        
        let summaryText = node.roleSummary || '';
        if(!summaryText && Array.isArray(node.description) && node.description.length > 0) {
             const lastEntry = [...node.description].reverse().find(d => d && typeof d.msg === 'string');
             if(lastEntry) summaryText = lastEntry.msg.replace('[ROLE_DESC_SAVE]','');
        }

        let avatarsHTML = '';
        if (profiles.length > 0) {
            const maxDisplay = 4;
            const displayProfiles = profiles.slice(0, maxDisplay);
            const remaining = profiles.length - maxDisplay;

            avatarsHTML = `<div class="sb-avatars-row">`;
            displayProfiles.forEach((p, i) => {
            // 1. Resolve Identity
            const uid = p.userId || p.user_id || p._id || p.id || `unknown-${i}`;
            
            // 2. AGGRESSIVE NAME RESOLVER
            // We check root, then userData, then even deeper if necessary
            const ud = p.userData || {};
            
            const first = p.firstName || p.first_name || ud.firstName || ud.first_name || '';
            const last = p.lastName || p.last_name || ud.lastName || ud.last_name || '';
            const combined = `${first} ${last}`.trim();
            
            const rawName = p.name || p.fullName || p.display_name || ud.name || ud.fullName || '';
            const emailPart = p.email ? p.email.split('@')[0] : '';

            // This priority list ensures we find SOMETHING better than "Talent"
            const resolvedName = (combined || rawName || emailPart || '').trim();
            
            // FINAL FALLBACK: If we still have nothing, we use the word "Profile" 
            // but we log the object so we can see why it's empty
            if (!resolvedName) {
                console.warn("SandboxView: Could not find name for profile object:", p);
            }
            
            const finalHoverName = resolvedName || "User Profile";

            // 3. Resolve Image
            let imgUrl = '';
            const imgFile = p.profilePictureUrl || p.profilePicture || ud.profilePictureUrl || ud.profilePicture || p.avatar;

            if (imgFile && typeof imgFile === 'string' && imgFile !== "null" && imgFile.length > 3) {
                imgUrl = imgFile.startsWith('http') ? imgFile : `https://gtbrdd-storage.s3.us-west-2.amazonaws.com/editor/profile-pictures/${imgFile}`;
            } else {
                // Unique seed based on UID ensures unique faces
                const seed = encodeURIComponent(uid);
                imgUrl = `https://hostedboringavatars.vercel.app/api/beam?name=${seed}&colors=F2C244,E77E23,7367F0,28C76F,EA5455`;
            }

            // The 'title' attribute here is what controls the hover text
            avatarsHTML += `<img src="${imgUrl}" class="sb-avatar" style="z-index: ${maxDisplay - i};" title="${finalHoverName}" data-uid="${uid}" />`;
        });
            if (remaining > 0) {
                avatarsHTML += `<div class="sb-avatar-more" title="${remaining} more assigned">+${remaining}</div>`;
            }
            avatarsHTML += `</div>`;
        } else {
            avatarsHTML = `<div class="sb-avatars-row"><span style="font-size:11px; color:#94a3b8; font-style:italic;">Unassigned</span></div>`;
        }

        let badgeHTML = '';
        if (isRootNode) {
            badgeHTML = `<div class="sb-badge" style="background:#FFFBEB; color:#B45309; border:1px solid #FCD34D;">${svgUser} <b>${displayCount} Headcount</b></div>`;
        } else {
            badgeHTML = `<div class="sb-badge" style="background:#e0f2fe; color:#0284c7;">${svgUser} ${displayCount}</div>`;
        }

        const headerColor = node.displaySettings?.cc || this.displaySettings?.cc || '#48A9C5';

        el.innerHTML = `
            <div class="sb-node-header" style="background-color: ${headerColor};">
                <span class="sb-title-span" title="${node.title}">${node.title}</span>
                <div class="sb-header-actions">
                    ${!isRootNode ? `<button class="sb-act-btn edit-btn" title="Edit Role">${svgEdit}</button>` : ''}
                    ${!isRootNode ? `<button class="sb-act-btn del-btn" title="Delete">${svgTrash}</button>` : ''}
                </div>
            </div>
            <div class="sb-node-body" title="Click to view details">
                <div class="sb-desc-box">${summaryText || "No description provided."}</div>
                <div class="sb-footer-row">
                    ${avatarsHTML}
                    <div class="sb-stats">${badgeHTML}</div>
                </div>
            </div>
        `;

        const handleEditSuccess = async (updatedData) => {
            try {
                if (this.actions.onUpdate) {
                    await this.actions.onUpdate(node.selfId, updatedData);
                }
                const targetNode = this.data.find(n => n.selfId === node.selfId);
                if (targetNode) {
                    Object.assign(targetNode, updatedData);
                }
                // Refresh Canvas Data
                window.dispatchEvent(new CustomEvent('kvdr-sandbox-refresh', { detail: { graphData: this.data } }));
            } catch (error) {
                console.error("Failed to update and refresh the role:", error);
            }
        };

        const nodeBody = el.querySelector('.sb-node-body');
        nodeBody.addEventListener('click', (e) => {
            e.stopPropagation();

            if (e.target.classList.contains('sb-avatar')) {
                const uid = e.target.dataset.uid;
                window.dispatchEvent(new CustomEvent('show-talent-card', { detail: { userId: uid } }));
                if (this.actions.onProfileClick) {
                    this.actions.onProfileClick(node.selfId, uid);
                }
            } else {
                if (!isRootNode) {
                    RolePopupManager.showCreateOrEditPopup('EDIT', node, handleEditSuccess);
                }
            }
        });

        if (!isRootNode) {
            el.querySelector('.edit-btn').onclick = (e) => {
                e.stopPropagation();
                RolePopupManager.showCreateOrEditPopup('EDIT', node, handleEditSuccess);
            };

            el.querySelector('.del-btn').onclick = (e) => { 
                e.stopPropagation();
                let subRoles = this.data.filter(n => n.parentId === node.selfId).length;
                RolePopupManager.showDeleteRolePopup(node, subRoles, displayCount, () => {
                    this.actions.onDelete && this.actions.onDelete(node.selfId); 
                });
            };
        }

        const addBtn = document.createElement('div');
        addBtn.className = 'sb-add-btn';
        addBtn.dataset.nodeId = node.selfId; 
        addBtn.innerHTML = `<span>+</span>`;
        addBtn.style.left = `${node.x + (this.cardWidth / 2)}px`;
        addBtn.style.top = `${node.y + this.cardHeight}px`;
        
        addBtn.onclick = (e) => { 
            e.stopPropagation(); 
            RolePopupManager.showCreateOrEditPopup('CREATE', { title: "" }, (newData) => {
                this.actions.onAdd && this.actions.onAdd(node.selfId, newData);
            });
        };

        this.canvas.appendChild(el);
        this.canvas.appendChild(addBtn);
    }

    drawLink(p, c) {
        const pX = p.x + (this.cardWidth / 2);
        const pY = p.y + this.cardHeight;
        const cX = c.x + (this.cardWidth / 2);
        const cY = c.y;
        const midY = pY + (cY - pY) / 2;
        const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
        path.setAttribute("d", `M ${pX} ${pY} L ${pX} ${midY} L ${cX} ${midY} L ${cX} ${cY}`);
        path.setAttribute("class", "sb-connector");
        this.svgLayer.appendChild(path);
    }

    drawMinimap() {
        if(!this.treeRoot || !this.minimap) return;
        this.minimap.innerHTML = '';
        
        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
        const traverse = (n) => {
            if(n.x < minX) minX = n.x;
            if(n.x + this.cardWidth > maxX) maxX = n.x + this.cardWidth;
            if(n.y < minY) minY = n.y;
            
            // FIX: Account for the '+' button (28px height + 2px margin) below the card
            const addBtnAndMargin = 30;
            if(n.y + this.cardHeight + addBtnAndMargin > maxY) maxY = n.y + this.cardHeight + addBtnAndMargin;

            n.children.forEach(traverse);
        };
        traverse(this.treeRoot);
        
        minX -= 300; maxX += 300; minY -= 300; maxY += 300;
        const treeW = maxX - minX;
        const treeH = maxY - minY;
        const scale = Math.min(200 / treeW, 150 / treeH);
        
        const drawMini = (n) => {
            const d = document.createElement('div');
            d.className = 'sb-mini-node';
            d.style.left = `${(n.x - minX) * scale}px`;
            d.style.top = `${(n.y - minY) * scale}px`;
            d.style.width = `${this.cardWidth * scale}px`;
            d.style.height = `${this.cardHeight * scale}px`;
            this.minimap.appendChild(d);
            n.children.forEach(drawMini);
        };
        drawMini(this.treeRoot);
        
        this.miniViewport = document.createElement('div');
        this.miniViewport.className = 'sb-mini-viewport';
        this.minimap.appendChild(this.miniViewport);
        this.mapState = { minX, minY, scale };
        this.updateMinimapViewport();
    }

    updateMinimapViewport() {
        if(!this.miniViewport || !this.mapState) return;
        const screenW = this.wrapper.clientWidth;
        const screenH = this.wrapper.clientHeight;
        const worldLeft = -this.panX / this.zoom;
        const worldTop = -this.panY / this.zoom;
        
        this.miniViewport.style.left = `${(worldLeft - this.mapState.minX) * this.mapState.scale}px`;
        this.miniViewport.style.top = `${(worldTop - this.mapState.minY) * this.mapState.scale}px`;
        this.miniViewport.style.width = `${(screenW / this.zoom) * this.mapState.scale}px`;
        this.miniViewport.style.height = `${(screenH / this.zoom) * this.mapState.scale}px`;
    }

    handleZoom(delta) {
        this.zoom = Math.min(Math.max(0.2, this.zoom + delta), 2);
        this.updateTransform();
    }

    resetView() {
        if (!this.treeRoot || !this.wrapper || this.wrapper.clientWidth === 0) return;

        const wrapperW = this.wrapper.clientWidth;
        const wrapperH = this.wrapper.clientHeight;

        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
        const traverse = (n) => {
            if (n.x < minX) minX = n.x;
            if (n.x + this.cardWidth > maxX) maxX = n.x + this.cardWidth;
            if (n.y < minY) minY = n.y;

            // More robust height calculation for the '+' button below the card
            const addBtnHeightWithMargin = 40; 
            if (n.y + this.cardHeight + addBtnHeightWithMargin > maxY) {
                maxY = n.y + this.cardHeight + addBtnHeightWithMargin;
            }
            
            n.children.forEach(traverse);
        };
        traverse(this.treeRoot);

        const treeWidth = maxX - minX;
        const treeHeight = maxY - minY;
        
        // Use dynamic padding based on viewport size for better fitting
        const PADDING_X = wrapperW * 0.05; // 5% horizontal padding
        const PADDING_Y = wrapperH * 0.05; // 5% vertical padding

        const zoomX = wrapperW / (treeWidth + PADDING_X * 2);
        const zoomY = wrapperH / (treeHeight + PADDING_Y * 2);
        this.zoom = Math.min(zoomX, zoomY, 1);

        const treeCenterX = minX + treeWidth / 2;
        const treeCenterY = minY + treeHeight / 2;

        this.panX = (wrapperW / 2) - (treeCenterX * this.zoom);
        this.panY = (wrapperH / 2) - (treeCenterY * this.zoom);

        this.updateTransform();
    }

    updateTransform() {
        this.canvas.style.transform = `translate(${this.panX}px, ${this.panY}px) scale(${this.zoom})`;
        this.updateMinimapViewport();
    }

    attachEvents() {
    let startX=0, startY=0;

    this.wrapper.addEventListener('mousedown', (e) => {
        // Check if we are clicking an actual node or button to prevent selection starting on them
        if(e.target.closest('.sb-node') || e.target.closest('button') || e.target.closest('.sb-add-btn')) return;

        if (this.activeTool === 'select') {
            console.log("Marquee Selection Started"); // Debug Log
            this.marquee.startSelection(e);
        } else {
            this.isBackgroundDragging = true;
            startX = e.clientX - this.panX;
            startY = e.clientY - this.panY;
            this.wrapper.style.cursor = 'grabbing';
        }
    });
        
        window.addEventListener('mousemove', (e) => {
            if(this.isBackgroundDragging) {
                e.preventDefault();
                this.panX = e.clientX - startX;
                this.panY = e.clientY - startY;
                this.updateTransform();
            }
        });
        
        window.addEventListener('mouseup', () => { 
            if (this.isBackgroundDragging) {
                this.isBackgroundDragging = false; 
                this.wrapper.style.cursor = 'grab';
            }
        });
        
        this.wrapper.addEventListener('wheel', (e) => {
            if (e.ctrlKey) { e.preventDefault(); this.handleZoom(e.deltaY > 0 ? -0.1 : 0.1); }
        });

        if (this.resizeObserver) this.resizeObserver.disconnect();
    
        this.resizeObserver = new ResizeObserver(() => {
            // We debounce this call slightly to avoid rapid-fire resets during resize
            clearTimeout(this.resizeTimeout);
            this.resizeTimeout = setTimeout(() => this.resetView(), 100);
        });
        
        this.resizeObserver.observe(this.wrapper);
    }

    _updateNodeSelectionStyles(selectedIds) {
        const allNodeElements = this.wrapper.querySelectorAll('.sb-node');
        allNodeElements.forEach(nodeEl => {
            if (selectedIds.has(nodeEl.dataset.id)) {
                nodeEl.classList.add('is-selected');
            } else {
                nodeEl.classList.remove('is-selected');
            }
        });
    }
}